import React, { useState, useContext } from 'react';
import {
    Box,
    Heading,
    Group,
    Text,
    useNotification,
    Alert,
    Button,
    Collapse,
    Paragraph,
    Divider,
} from '@landr/maestro';
import { EventIds } from 'types/enums';
import { Trans, t } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { CONFIG } from 'utils/config';
import { log } from 'utils/log';
import { i18n } from 'utils/setupTranslations';
import { AuthenticationContext } from 'contexts/AuthenticationContext';
import { useNewPackFormSchema } from 'hooks/useNewPackFormSchema';
import authenticationService from 'utils/authentication';
import { NewPackFormFields } from './NewPack.types';
import NewPackForm, { initialNewPackFormValues } from './NewPackForm';

export const NewPack: React.FC = () => {
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const {
        authToken: token,
        isAuthenticated,
        providerName,
        canUploadCsv,
    } = useContext(AuthenticationContext);

    // HOOKS
    const { notify } = useNotification();
    const newPackFormSchema = useNewPackFormSchema();

    const notification = (error: any): void => {
        let notification;

        switch (error?.status) {
            case 400:
                notification = t`Submission failed. Please contact LANDR for support. ${error?.title} `;
                break;

            default:
                notification = t`Submission failed. Please try again. If the issue persist please contact LANDR for support.`;
                break;
        }

        notify({
            variant: 'danger',
            content: i18n._(notification),
            timeout: 60000,
        });
    };

    const submit = async (
        values: NewPackFormFields,
        setSubmitting: (isSubmitting: boolean) => void,
    ): Promise<void> => {
        const {
            artworkUpload,
            csvUpload,
            demoUpload,
            zipUpload,
            exclusivity,
            useSfxLexicon,
            ...sanitizedValues
        } = values;

        const body = JSON.stringify({
            ...sanitizedValues,
            canUploadCsv,
            exclusivity,
            useSfxLexicon,
        });

        await fetch(`${CONFIG.INGESTION_API_URL}/v1/packs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: body,
        })
            .then(async (response) => {
                if (response.status >= 400 && response.status < 600) {
                    return response.json();
                }
                setSubmitSuccessful(true);
                setSubmitting(false);
                log.info('Submission success', EventIds.SubmissionSuccess, {
                    body: body,
                });
            })
            .then((error) => {
                if (error) {
                    notification(error);
                    const errorMsg = `Server return a ${error.status}: ${error.title}`;
                    throw new Error(errorMsg);
                }
            })
            .catch((error) => {
                log.error(
                    'Submission failed',
                    EventIds.SubmissionFailed,
                    error,
                );
                setSubmitSuccessful(false);
                setSubmitting(false);
            });
    };

    if (!isAuthenticated) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center">
                <Text mb="jb" size="xl">
                    <Trans>
                        You need to be logged in to access the New Pack Upload
                        page.
                    </Trans>
                </Text>

                <Button onClick={void authenticationService.login}>
                    <Trans>Login</Trans>
                </Button>
            </Box>
        );
    }

    const listItemBulletStyle = { listStyle: 'disc', marginLeft: '24px' };
    const listItemBulletSecondStyle = { listStyle: 'disc', marginLeft: '48px' };
    const listItemWrapper = { marginBottom: '24px' };
    const listItemTitle = { fontWeight: 600, margin: '0px' };
    const listItemSecondTitle = { fontWeight: 600, margin: '24px 0px 0px 0px' };

    return (
        <Formik
            initialValues={initialNewPackFormValues}
            validationSchema={newPackFormSchema}
            onSubmit={(values: NewPackFormFields, { setSubmitting }): void => {
                submit(values, setSubmitting);
            }}
            onReset={(): void => {
                setSubmitSuccessful(false);
            }}
        >
            {(formikProps: FormikProps<NewPackFormFields>): JSX.Element => (
                <>
                    {!submitSuccessful && (
                        <>
                            <Group
                                direction="vertical"
                                gutter="xl"
                                mb={'xl'}
                                maxWidth="900px"
                            >
                                <Group.Item>
                                    <Heading
                                        as="h1"
                                        size="jb"
                                        textAlign="left"
                                        mb={'lg'}
                                    >
                                        <Trans>Submit New Samples Pack</Trans>
                                    </Heading>
                                </Group.Item>
                                <Group.Item>
                                    <Heading
                                        as="h2"
                                        size="xl"
                                        textAlign="left"
                                        mb="xs"
                                    >
                                        <Trans>Welcome {providerName}</Trans>
                                    </Heading>
                                    <Paragraph color="neutral.400" size="lg">
                                        <Trans>
                                            Hey there! Exciting news – you're
                                            about to share a new pack with us!
                                            We can't wait to dive in and share
                                            it with our awesome users. Big
                                            thanks for getting your files and
                                            info ready following the
                                            instructions. Got questions? Drop us
                                            a line anytime at{' '}
                                            <a href="mailto:samples@landr.com">
                                                samples@landr.com
                                            </a>
                                            . We're here to help!
                                        </Trans>
                                    </Paragraph>
                                </Group.Item>
                            </Group>
                            <Group direction="vertical" gutter="lg">
                                <Group.Item>
                                    <Heading
                                        as="h2"
                                        size="xl"
                                        textAlign="left"
                                        marginBottom="0"
                                    >
                                        <Trans>Before you start</Trans>
                                    </Heading>
                                    <Paragraph
                                        color="neutral.400"
                                        size="lg"
                                        mb="xxs"
                                    >
                                        <Trans>
                                            Please read and prepare your files
                                            with these instructions. <br />
                                            You can{' '}
                                            <a
                                                href="./FolderStructure.zip"
                                                download
                                            >
                                                download this zip structure file
                                            </a>{' '}
                                            to have an example to follow
                                        </Trans>
                                    </Paragraph>
                                </Group.Item>
                                <Group.Item>
                                    <Alert palette="success" isInline>
                                        <Alert.Title>
                                            <Trans>
                                                Fastest your approval request by
                                                following our guidelines and
                                                preparing your files with these
                                                instructions.
                                            </Trans>
                                        </Alert.Title>
                                    </Alert>
                                </Group.Item>
                                <Group.Item>
                                    <Collapse>
                                        <Collapse.Title>
                                            <Text>
                                                <Trans>
                                                    Is it your first delivery?
                                                    We need some others elements
                                                    to complete your label page.
                                                </Trans>
                                            </Text>
                                        </Collapse.Title>
                                        <Collapse.Content>
                                            <Paragraph>
                                                <Trans>
                                                    Please send an email to{' '}
                                                    <a href="mailto:samples@landr.com">
                                                        samples@landr.com
                                                    </a>{' '}
                                                    with:{' '}
                                                    <ul>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Label logo artwork
                                                            (labelname_artwork,
                                                            jpg or png, 500x500
                                                            minimum, black on
                                                            white background)
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Label description
                                                        </li>
                                                    </ul>
                                                </Trans>
                                            </Paragraph>
                                        </Collapse.Content>
                                    </Collapse>
                                </Group.Item>
                                <Group.Item>
                                    <Collapse>
                                        <Collapse.Title>
                                            <Text>
                                                <Trans>Audio formatting</Trans>
                                            </Text>
                                        </Collapse.Title>
                                        <Collapse.Content>
                                            <Paragraph>
                                                <Trans>
                                                    General (both loops and one
                                                    shots)
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            WAV24-bit/44.1kHz.
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Do not submit
                                                            samples that are
                                                            clipped
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Sample must start at
                                                            zero crossing point
                                                            of the waveform
                                                            (apply short fade if
                                                            clipping still
                                                            occurs)
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Sample must end at
                                                            zero crossing point
                                                            (apply short fade if
                                                            necessary)
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Do not normalize the
                                                            audio
                                                        </li>{' '}
                                                    </ul>
                                                    Loops
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Start all loops on
                                                            the first downbeat
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            No extensive silence
                                                            at the start of
                                                            loops
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            No extensive silence
                                                            at the end of loops
                                                        </li>{' '}
                                                    </ul>
                                                    One shots
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            No silence at the
                                                            start or end of one
                                                            shots
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Do not cut the
                                                            initial transient
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Include entire tail
                                                            on all one shots
                                                        </li>
                                                    </ul>
                                                </Trans>
                                            </Paragraph>
                                        </Collapse.Content>
                                    </Collapse>
                                </Group.Item>
                                <Group.Item>
                                    <Collapse>
                                        <Collapse.Title>
                                            <Text>
                                                <Trans>
                                                    WAV file naming conventions
                                                </Trans>
                                            </Text>
                                        </Collapse.Title>
                                        <Collapse.Content>
                                            <Paragraph>
                                                <Trans>
                                                    <p style={listItemWrapper}>
                                                        Please adhere to the
                                                        naming convention
                                                        demonstrated below to
                                                        ensure our system
                                                        accurately tags your
                                                        sample.
                                                    </p>
                                                    <p style={listItemTitle}>
                                                        General
                                                    </p>
                                                    Samples should include the
                                                    pack initials and the
                                                    instrument name in the file
                                                    name:
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            SampleName_InstrumentName_keyCmin_120bpm.wav
                                                        </li>{' '}
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            Loremipsum_Electric_Guitar_Ensemble_1_keyBm_120bpm.wav
                                                        </li>
                                                    </ul>
                                                    <p style={listItemTitle}>
                                                        Loops
                                                    </p>
                                                    Loops need to have the BPM
                                                    mentioned in the file name.{' '}
                                                    <br />
                                                    All tonal loops with pitched
                                                    instruments need to also
                                                    have the key in the file
                                                    name:
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            SampleName_InstrumentName_keyAMaj_110bpm.wav
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            Loremipsum_Electric_Guitar_Ensemble_2_keyBm_120bpm.wav
                                                        </li>
                                                    </ul>
                                                    <p style={listItemTitle}>
                                                        One Shots
                                                    </p>
                                                    One shot samples do not need
                                                    the BPM. Oneshot of pitched
                                                    instruments needs the chord
                                                    or note in the file name:
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            SampleName_InstrumentName_keyAMaj.wav
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletSecondStyle
                                                            }
                                                        >
                                                            Loremipsum_InstrumentName_keyA.wav
                                                        </li>
                                                    </ul>
                                                </Trans>
                                            </Paragraph>
                                        </Collapse.Content>
                                    </Collapse>
                                </Group.Item>
                                <Group.Item>
                                    <Collapse>
                                        <Collapse.Title>
                                            <Text>Folder Structure</Text>
                                        </Collapse.Title>
                                        <Collapse.Content>
                                            <Paragraph>
                                                <Trans>
                                                    <p style={listItemTitle}>
                                                        Create a main folder for
                                                        your collection
                                                    </p>
                                                    PackName_LabelName
                                                    <p
                                                        style={
                                                            listItemSecondTitle
                                                        }
                                                    >
                                                        Create 2 subfolders
                                                        inside the main folder
                                                    </p>
                                                    <ul style={listItemWrapper}>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Loops- drop all
                                                            loops in this
                                                            subfolder.
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            One Shots- drop all
                                                            one shots in this
                                                            subfolder
                                                        </li>
                                                        <li
                                                            style={
                                                                listItemBulletStyle
                                                            }
                                                        >
                                                            Special mention for
                                                            Construction Kits,
                                                            if working with
                                                            construction kits,
                                                            you can include the
                                                            construction kits in
                                                            those folders,
                                                            please separate Loop
                                                            and One shots in the
                                                            two different
                                                            folders.
                                                        </li>
                                                    </ul>
                                                    <p
                                                        style={
                                                            listItemSecondTitle
                                                        }
                                                    >
                                                        Packs need to be more
                                                        than 30 samples in size
                                                    </p>
                                                    <p
                                                        style={
                                                            listItemSecondTitle
                                                        }
                                                    >
                                                        You can{' '}
                                                        <a href="./FolderStructure.zip">
                                                            download this zip
                                                            structure file
                                                        </a>{' '}
                                                        to have an example to
                                                        follow
                                                    </p>
                                                </Trans>
                                            </Paragraph>
                                        </Collapse.Content>
                                    </Collapse>
                                </Group.Item>
                            </Group>
                            <Divider py="jb" />
                            <Group>
                                <Group.Item>
                                    <Heading
                                        as="h2"
                                        size="xl"
                                        textAlign="left"
                                        mb="lg"
                                    >
                                        <Trans>Pack informations</Trans>
                                    </Heading>
                                    <NewPackForm {...formikProps} />
                                </Group.Item>
                            </Group>
                        </>
                    )}
                    {submitSuccessful && (
                        <Group
                            direction="vertical"
                            gutter="jb"
                            display="flex"
                            justifyContent={'center'}
                            alignItems={'center'}
                            textAlign="center"
                            mt="lg"
                        >
                            <Group.Item>
                                <Heading as="h2" size="jb">
                                    <Trans>
                                        Thank you for submitting your pack!
                                    </Trans>
                                </Heading>
                                <Paragraph size="lg" color="neutral.300">
                                    You've done your part, and now it's our
                                    turn! Our team is on it, working to get your
                                    pack up on our platform. <br />
                                    If we need anything from you, we'll be sure
                                    to reach out.
                                </Paragraph>
                            </Group.Item>
                            <Group.Item>
                                <Button
                                    isRounded={true}
                                    onClick={formikProps.handleReset}
                                >
                                    <Trans>Submit another one</Trans>
                                </Button>
                            </Group.Item>
                        </Group>
                    )}
                </>
            )}
        </Formik>
    );
};
